import { sortBy, sumBy, uniqBy } from 'lodash';
import moment from 'moment';
import React from 'react';

import {
  IntegrationStatusDto,
  JobCostCodeSummaryGpDto,
  JobCostDetailDto,
  JobPoDataDto,
} from 'src/api/FiretrolApiClient';

import {
  CATEGORIES,
  LABOR_CATEGORY,
  getCategoryByCode,
} from 'src/routes/CTC/ViewCTCJob/constants';

/**
 * Sanitizes a JobCostCodeSummaryGpDto record by replacing null/undefined
 * numeric values with 0. Prevents NaN values when rendering or summing.
 */
function sanitizeCostCodeSummary(summary: JobCostCodeSummaryGpDto) {
  return {
    ...summary,
    costCode: summary.costCode ?? '',
    costCodeDescription: summary.costCodeDescription ?? '',
    originalEstimateCost: Number(summary.originalEstimateCost) || 0,
    revisedEstimateCost: Number(summary.revisedEstimateCost) || 0,
    committedCost: Number(summary.committedCost) || 0,
    jtdCost: Number(summary.jtdCost) || 0,
    mtdCost: Number(summary.mtdCost) || 0,
    estHours: Number(summary.estHours) || 0,
    jtdHours: Number(summary.jtdHours) || 0,
    mtdHours: Number(summary.mtdHours) || 0,
    estimatedRate: Number(summary.estimatedRate) || 0,
    ctcHours: Number(summary.ctcHours) || 0,
    jtdRate: Number(summary.jtdRate) || 0,
    ctc: Number(summary.ctc) || 0,
    priorEarned: Number(summary.priorEarned) || 0,
  };
}

export interface AggregateRow {
  costCode: string;
  description: string;
  originalEstimateCost: number;
  revisedEstimateCost: number;
  jtdCost: number;
  mtdAmount: number;
  estHours: number;
  jtdHours: number;
  mtdHours: number;
  estimatedRate: number;
  ctcHours: number;
  jtdRate: number;
  ctc: number;
  remainingPoBalance: number;
  cost: number;
  diff: number;
  isLabor: boolean;
}

export interface AggregateTotals {
  originalEstimateCost: number;
  revisedEstimateCost: number;
  remainingPoBalance: number;
  mtdAmount: number;
  jtdCost: number;
  estHours: number;
  jtdHours: number;
  mtdHours: number;
  remain: number;
  ctc: number;
  cost: number;
  diff: number;
}

export interface CategoryGroup {
  category: string;
  isLabor: boolean;
  rows: AggregateRow[];
  totals: AggregateTotals;
}

const emptyTotals = (): AggregateTotals => ({
  originalEstimateCost: 0,
  revisedEstimateCost: 0,
  remainingPoBalance: 0,
  mtdAmount: 0,
  jtdCost: 0,
  estHours: 0,
  jtdHours: 0,
  mtdHours: 0,
  remain: 0,
  ctc: 0,
  cost: 0,
  diff: 0,
});

function buildAggregateRow(
  summary: JobCostCodeSummaryGpDto,
  details: Record<string, JobCostDetailDto[]>,
  materials: Record<string, JobPoDataDto[]>,
  currMonth: moment.Moment,
): AggregateRow {
  const sanitized = sanitizeCostCodeSummary(summary);
  const category = getCategoryByCode(sanitized.costCode);
  const isLabor = category === LABOR_CATEGORY;

  const detailItems = (details[sanitized.costCode] || []).map((r) => ({
    ...r,
    numberAmount: Number(r.amount),
  }));
  const currentMonthDetails = detailItems.filter((d) => {
    const tmp = new Date(String(d.docdate)?.slice(0, 10));
    return moment(
      new Date(tmp.getTime() - tmp.getTimezoneOffset() * -60000),
    ).isSame(currMonth, 'month');
  });
  const mtdAmount = sumBy(currentMonthDetails, 'numberAmount') || 0;

  const poList = uniqBy(materials[sanitized.costCode] || [], 'ponumber').map(
    (m) => ({
      ...m,
      poAmount: Number(m.poAmount),
      remainingPoBalance: Number(m.remainingPoBalance),
    }),
  );
  const remainingPoBalance = sumBy(poList, 'remainingPoBalance');

  const cost = sanitized.ctc + sanitized.jtdCost;
  const diff = sanitized.revisedEstimateCost - cost;

  return {
    costCode: sanitized.costCode,
    description: sanitized.costCodeDescription,
    originalEstimateCost: sanitized.originalEstimateCost,
    revisedEstimateCost: sanitized.revisedEstimateCost,
    jtdCost: sanitized.jtdCost,
    mtdAmount,
    estHours: sanitized.estHours,
    jtdHours: sanitized.jtdHours,
    mtdHours: sanitized.mtdHours,
    estimatedRate: sanitized.estimatedRate,
    ctcHours: sanitized.ctcHours,
    jtdRate: sanitized.jtdRate,
    ctc: sanitized.ctc,
    remainingPoBalance,
    cost,
    diff,
    isLabor,
  };
}

function addRowToTotals(totals: AggregateTotals, row: AggregateRow): void {
  totals.originalEstimateCost += row.originalEstimateCost;
  totals.revisedEstimateCost += row.revisedEstimateCost;
  totals.jtdCost += row.jtdCost;
  totals.mtdAmount += row.mtdAmount;
  totals.ctc += row.ctc;
  totals.cost += row.cost;
  totals.diff += row.diff;
  totals.estHours += row.estHours;
  totals.jtdHours += row.jtdHours;
  totals.mtdHours += row.mtdHours;
  totals.remain += Math.round(row.estHours - row.jtdHours);
  totals.remainingPoBalance += row.remainingPoBalance;
}

interface UseCostCodeAggregatesArgs {
  summaryList: JobCostCodeSummaryGpDto[];
  details: Record<string, JobCostDetailDto[]>;
  materials: Record<string, JobPoDataDto[]>;
  status: IntegrationStatusDto | null;
}

export interface CostCodeAggregates {
  groups: CategoryGroup[];
  grandTotals: AggregateTotals;
}

/**
 * Derives per-cost-code, per-category, and grand-total aggregates from the
 * raw CTC payload. Category groups are emitted in the canonical CATEGORIES
 * order; rows within a group are sorted by cost code and deduped. Grand
 * totals match the legacy renderFooter math (cost codes whose category is
 * unknown are excluded from labor/non-labor branches but still counted).
 */
export function useCostCodeAggregates({
  summaryList,
  details,
  materials,
  status,
}: UseCostCodeAggregatesArgs): CostCodeAggregates {
  return React.useMemo(() => {
    const currMonth = status?.mtdCalcMonth
      ? moment().month(status.mtdCalcMonth - 1)
      : moment();

    const groups: CategoryGroup[] = CATEGORIES.map(({ category, code }) => {
      const isLabor = category === LABOR_CATEGORY;
      const codeRows = sortBy(
        uniqBy(
          summaryList.filter((s) => s.costCode.slice(0, 2) === code),
          'costCode',
        ),
        'costCode',
      ).map((s) => buildAggregateRow(s, details, materials, currMonth));

      const totals = emptyTotals();
      codeRows.forEach((row) => addRowToTotals(totals, row));

      return { category, isLabor, rows: codeRows, totals };
    });

    const grandTotals = emptyTotals();
    summaryList.forEach((summary) => {
      const category = getCategoryByCode(summary.costCode);
      if (!category) return;
      const isLabor = category === LABOR_CATEGORY;
      const row = buildAggregateRow(summary, details, materials, currMonth);

      grandTotals.originalEstimateCost += row.originalEstimateCost;
      grandTotals.revisedEstimateCost += row.revisedEstimateCost;
      grandTotals.jtdCost += row.jtdCost;
      grandTotals.mtdAmount += row.mtdAmount;
      grandTotals.ctc += row.ctc;
      grandTotals.cost += row.cost;
      grandTotals.diff += row.diff;
      if (!isLabor) {
        grandTotals.remainingPoBalance += row.remainingPoBalance;
      }
      if (isLabor) {
        grandTotals.estHours += row.estHours;
        grandTotals.jtdHours += row.jtdHours;
        grandTotals.mtdHours += row.mtdHours;
        grandTotals.remain += Math.round(row.estHours - row.jtdHours);
      }
    });

    return { groups, grandTotals };
  }, [summaryList, details, materials, status]);
}
